// Shared form utilities to eliminate duplication across Contact, ContactModal, and Services components

// Services data for the multi-select - shared across all forms
export const allServices = [
  { value: 'roofing', label: 'Roofing' },
  { value: 'windows', label: 'Windows' },
  { value: 'siding', label: 'Siding' },
  { value: 'gutters', label: 'Gutters' },
  { value: 'repairs', label: 'Repairs' }
];

// Email validation function
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation function
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
};

// Form validation function
export const validateForm = (formData, includeMessage = true) => {
  const errors = {};
  
  // Name validation
  if (!formData.name.trim()) {
    errors.name = 'Name is required';
  } else if (formData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }
  
  // Email and Phone validation - either one is required
  const hasEmail = formData.email.trim();
  const hasPhone = formData.phone.trim();
  
  if (!hasEmail && !hasPhone) {
    errors.email = 'Either email or phone number is required';
    errors.phone = 'Either email or phone number is required';
  } else {
    // Validate email if provided
    if (hasEmail && !isValidEmail(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Validate phone if provided
    if (hasPhone && !isValidPhone(formData.phone.trim())) {
      errors.phone = 'Please enter a valid phone number';
    }
  }
  
  // Services validation
  if (!formData.services || formData.services.length === 0) {
    errors.services = 'Please select at least one service';
  }
  
  // Message validation (only if includeMessage is true)
  if (includeMessage) {
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Focus on first error field
export const focusFirstError = (errors, refs, formType = 'main') => {
  // Only focus if there are actual errors
  if (!errors || Object.keys(errors).length === 0) {
    return;
  }
  
  // Focus immediately to sync with shake effect
  const focusField = () => {
    // Priority order for focusing on error fields (matches DOM order)
    const fieldPriority = ['name', 'email', 'phone', 'services', 'message'];
    
    // Get the correct field ID based on form type
    const getFieldId = (fieldName, formType) => {
      switch (formType) {
        case 'contact':
          return `contact-${fieldName}`;
        case 'financing':
          return `financing-${fieldName}`;
        case 'modal':
          return `modal-${fieldName}`;
        default:
          return fieldName;
      }
    };
    
    if (formType === 'contact') {
      // For contact form, only look within the contact section
      const contactContainer = document.querySelector('#contact');
      if (!contactContainer) {
        return; // Can't find the contact section
      }
      
      // Check for contact form fields first
      for (const fieldName of fieldPriority) {
        if (errors[fieldName]) {
          if (fieldName === 'services') {
            // Look for service search input within the contact container
            const serviceSearchInput = contactContainer.querySelector('#service-search-contact');
            if (serviceSearchInput) {
              serviceSearchInput.focus();
              serviceSearchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          } else {
            const fieldId = getFieldId(fieldName, formType);
            const input = contactContainer.querySelector(`#${fieldId}`);
            if (input) {
              input.focus();
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
      
      // Check for additional dynamic fields in contact form
      for (const [fieldName, errorMessage] of Object.entries(errors)) {
        if (!fieldPriority.includes(fieldName) && errorMessage) {
          const fieldId = getFieldId(fieldName, formType);
          const input = contactContainer.querySelector(`#${fieldId}`);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
    } else if (formType === 'financing') {
      // For financing form, only look within the financing section
      const financingContainer = document.querySelector('.financing-section');
      if (!financingContainer) {
        return; // Can't find the financing section
      }
      
      // Check for financing form fields first
      for (const fieldName of fieldPriority) {
        if (errors[fieldName]) {
          if (fieldName === 'services') {
            // Look for service search input within the financing container
            const serviceSearchInput = financingContainer.querySelector('#service-search-financing');
            if (serviceSearchInput) {
              serviceSearchInput.focus();
              serviceSearchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          } else {
            const fieldId = getFieldId(fieldName, formType);
            const input = financingContainer.querySelector(`#${fieldId}`);
            if (input) {
              input.focus();
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
      
      // Check for additional dynamic fields in financing form
      for (const [fieldName, errorMessage] of Object.entries(errors)) {
        if (!fieldPriority.includes(fieldName) && errorMessage) {
          const fieldId = getFieldId(fieldName, formType);
          const input = financingContainer.querySelector(`#${fieldId}`);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
    } else if (formType === 'modal') {
      // For modal form, only look within the modal container
      const modalContainer = document.querySelector('.contact-modal');
      if (!modalContainer) {
        return; // Can't find the modal container
      }
      
      // Check for modal form fields first
      for (const fieldName of fieldPriority) {
        if (errors[fieldName]) {
          if (fieldName === 'services') {
            // Look for service search input within the modal container
            const serviceSearchInput = modalContainer.querySelector('#service-search-modal');
            if (serviceSearchInput) {
              serviceSearchInput.focus();
              serviceSearchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          } else {
            const fieldId = getFieldId(fieldName, formType);
            const input = modalContainer.querySelector(`#${fieldId}`);
            if (input) {
              input.focus();
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
      
      // Check for additional dynamic fields in modal form
      for (const [fieldName, errorMessage] of Object.entries(errors)) {
        if (!fieldPriority.includes(fieldName) && errorMessage) {
          const fieldId = getFieldId(fieldName, formType);
          const input = modalContainer.querySelector(`#${fieldId}`);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
    } else {
      // For main forms, use the original logic
      for (const fieldName of fieldPriority) {
        if (errors[fieldName]) {
          if (fieldName === 'services') {
            if (refs.serviceSearch) {
              refs.serviceSearch.focus();
              refs.serviceSearch.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          } else {
            const fieldId = getFieldId(fieldName, formType);
            const input = document.getElementById(fieldId);
            if (input) {
              input.focus();
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
      
      // Check for additional dynamic fields
      for (const [fieldName, errorMessage] of Object.entries(errors)) {
        if (!fieldPriority.includes(fieldName) && errorMessage) {
          const fieldId = getFieldId(fieldName, formType);
          const input = document.getElementById(fieldId);
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
    }
  };

  // Try to focus immediately
  focusField();
  
  // Fallback: try again after a short delay if the first attempt failed
  setTimeout(focusField, 50);
};


// Default form state
export const getDefaultFormState = () => ({
  name: '',
  email: '',
  phone: '',
  services: [],
  message: '',
  smsConsent: false
});

// Default form errors state
export const getDefaultFormErrors = () => ({
  name: '',
  email: '',
  phone: '',
  services: '',
  message: '',
  smsConsent: ''
});
